import React, { useState } from 'react';
import { Popover, Table, Tag, Typography, Empty, Spin, message } from 'antd';
import {
  CheckCircleOutlined, EyeOutlined, ExclamationCircleOutlined,
  ClockCircleOutlined, SendOutlined,
} from '@ant-design/icons';
import api from '../../services/api';

const { Text } = Typography;

// Traduz callType tecnico pro nome amigavel que o organizador reconhece
// (o card do jogo ja mostra "1ª", "2ª", "3ª", "4ª" na coluna de datas).
const CALL_LABEL = {
  EARLY_CALL: '1ª convocação',
  FIRST_CALL: '2ª convocação',
  SECOND_CALL: '3ª convocação',
  THIRD_CALL: '4ª convocação',
};

function deliveryTag(status, errCode) {
  if (errCode) return <Tag color="red"><ExclamationCircleOutlined /> falhou</Tag>;
  if (!status) return <Tag><SendOutlined /> enviada</Tag>;
  const s = String(status).toLowerCase();
  if (s === 'read') return <Tag color="blue"><EyeOutlined /> lida</Tag>;
  if (s === 'delivered') return <Tag color="green"><CheckCircleOutlined /> entregue</Tag>;
  if (s === 'sent') return <Tag><SendOutlined /> enviada</Tag>;
  if (s === 'failed') return <Tag color="red"><ExclamationCircleOutlined /> falhou</Tag>;
  return <Tag>{status}</Tag>;
}

function gameResponseTag(play, waiting) {
  if (waiting === true) return <Tag color="orange">espera</Tag>;
  if (play === true) return <Tag color="green">confirmou</Tag>;
  if (play === false) return <Tag color="red">fora</Tag>;
  return <Tag><ClockCircleOutlined /> sem resposta</Tag>;
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      + ' ' + d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch { return iso; }
}

/**
 * Envolve o children (icone ✅) com Popover que ao clicar mostra lista de
 * destinatarios da convocacao com delivery status + resposta no jogo.
 * Se sent=false, apenas renderiza children (sem popover).
 */
export default function CallRecipientsPopover({ gameId, callType, sent, children, endpointBase = '/organizer-games' }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    if (data) return; // cache client-side
    setLoading(true);
    try {
      const r = await api.get(`${endpointBase}/${gameId}/call-recipients/${callType}`);
      setData(r.data);
    } catch (err) {
      message.error(err?.response?.data?.error || 'Erro ao buscar');
    } finally { setLoading(false); }
  };

  const content = (
    <div style={{ minWidth: 340, maxWidth: 460, maxHeight: 420, overflowY: 'auto' }}>
      {loading && <div style={{ textAlign: 'center', padding: 16 }}><Spin size="small" /></div>}
      {!loading && data && data.total === 0 && (
        <Empty description="Nenhum envio registrado nessa convocação" imageStyle={{ height: 40 }} />
      )}
      {!loading && data && data.total > 0 && (
        <Table
          size="small"
          pagination={false}
          rowKey={(r) => `${r.player_id ?? 'null'}-${r.sent_at}`}
          dataSource={data.recipients}
          columns={[
            {
              title: 'Jogador',
              dataIndex: 'name',
              render: (v, r) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Text style={{ fontSize: 12 }}>{v}</Text>
                  {r.player_status && r.player_status !== 'ATIVO' && (
                    <Text type="secondary" style={{ fontSize: 10 }}>{r.player_status}</Text>
                  )}
                </div>
              ),
            },
            {
              title: 'Delivery',
              dataIndex: 'delivery_status',
              width: 100,
              render: (v, r) => (
                <div>
                  {deliveryTag(v, r.error_code)}
                  {r.status_updated_at && (
                    <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                      {fmtTime(r.status_updated_at)}
                    </div>
                  )}
                </div>
              ),
            },
            {
              title: 'Resposta',
              width: 100,
              render: (_, r) => gameResponseTag(r.game_play, r.game_waiting),
            },
          ]}
        />
      )}
    </div>
  );

  if (!sent) return children;

  return (
    <Popover
      content={content}
      title={
        <Text style={{ fontSize: 13 }}>
          Destinatários — {CALL_LABEL[callType] || callType}
          {data && (<Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>({data.total})</Text>)}
        </Text>
      }
      trigger="click"
      open={open}
      onOpenChange={(v) => { setOpen(v); if (v) load(); }}
      overlayStyle={{ maxWidth: 500 }}
    >
      <span style={{ cursor: 'pointer' }}>{children}</span>
    </Popover>
  );
}
